import { ADDENDUM_EDITOR_EDITABLE_STATUSES, type ReportStatuses } from '../state';
import type { WorklistItem, Addendum, ReportPicklist, AddendumContent } from 'generated/graphql';
import type { EnqueueToastFn } from 'common/ui/Toaster';
import clone from 'ramda/es/clone';
import { REPORT_FIELDS } from 'config/constants';
import {
  HEADING_VARIANTS,
  PLACEHOLDER_PLUGIN_ID,
  resolvePlaceholderID,
} from '../../RichTextEditor';
import type { SlateContent } from '../../RichTextEditor';
import { isNodeTypeIn, walkSlateContent } from '../../RichTextEditor/utils';
import { format } from 'date-fns/format';
import {
  getDefaultContentForHeading,
  insertHeading,
  normalizeText,
  toTitleCase,
} from '../../RichTextEditor/plugins/heading/utils/normalization';
import { HeadingLevel } from '../../RichTextEditor/plugins/heading/constants';
import {
  createInlineParagraph,
  isInlineParagraph,
  createParagraph,
  createParagraphWithChildren,
} from '../../RichTextEditor/plugins/paragraph/utils';
import { HEADING_PLUGIN_ID } from '../../RichTextEditor/plugins/heading/types';
import { Node, Text, Editor, Transforms } from '../../RichTextEditor/core';

import {
  endsWithNewline,
  endsWithNumber,
} from '../../RichTextEditor/stitching/normalizationHelpers';
import { newline } from './Fields.Context';
import { isEligibleNamedField } from '../../RichTextEditor/utils/fieldNaming';
import { PARAGRAPH_PLUGIN_ID } from '../../RichTextEditor/plugins/paragraph/types';
import {
  matchHeadingLikePrefix,
  type HeadingLikeMatch,
} from '../../RichTextEditor/plugins/headingDecoration/matcher';
import { LIST_PLUGIN_ID } from '../../RichTextEditor/plugins/list/types';
import { isSquareBracketType } from '../../RichTextEditor/constants';
import { createListItemNode } from '../../RichTextEditor/plugins/list/utils';
import { LIST_VARIANTS } from '../../RichTextEditor/plugins/list/constants';
import { logger } from 'modules/logger';
import { getSurroundingTextString } from '../../RichTextEditor/utils/getSurroundingTextString';
import { createElement } from 'react';
import { SironaSupportLink } from 'common/SironaSupportLink';
import type {
  ReportWorklistItem,
  ReportTemplate,
  LoadedReportWorklistItem,
} from 'hooks/useCurrentCaseReport';
import type { ParagraphPluginElementMutable } from '../../RichTextEditor/plugins/paragraph/types';

export const getDefaultSlateFieldChildren = (): SlateContent => {
  return [
    createParagraphWithChildren([
      {
        text: '',
      },
      {
        type: 'inlineBookmark',
        children: [
          {
            text: '',
          },
        ],
      },
      {
        text: '',
      },
    ]),
  ];
};

export const generateSlateHeadingSection = (text?: string): SlateContent => {
  return [
    insertHeading(HeadingLevel.H1, (text?.toUpperCase() ?? 'PLACEHOLDER') + ':'),
    ...getDefaultContentForHeading(text?.toLowerCase() ?? 'placeholder'),
  ];
};

export const generateEmptySlateContent = (): SlateContent => {
  return REPORT_FIELDS.map((field) =>
    generateSlateHeadingSection(HEADING_VARIANTS[field].name)
  ).flat();
};

export const normalizeSlateContent = (contentToNormalize?: SlateContent | null): SlateContent => {
  const content = clone(contentToNormalize ?? []);
  const normalizedContent: Array<ParagraphPluginElementMutable | SlateContent> = [];
  const fieldNames: Array<any> = [];
  let closestSubheadingText = null;
  let closestHeadingText = null;

  for (let i = 0; i < content.length; i++) {
    const node = content[i];

    if (node.children.length === 0) {
      node.children = [node.type === LIST_PLUGIN_ID ? createListItemNode() : { text: '' }];
    }

    if (
      node.type === LIST_PLUGIN_ID &&
      ![LIST_VARIANTS.ul, LIST_VARIANTS.ol].includes(node.variant)
    ) {
      node.variant = LIST_VARIANTS.ol;
      normalizedContent.push(node);
    } else if (node.type === HEADING_PLUGIN_ID) {
      if (Node.string(node).trim() === '') {
        i++;
        continue;
      }

      // Make sure headings end in a colon
      if (!node.children[0].text.trim().endsWith(':')) {
        node.children[0].text += ':';
      }

      if (node.level === HeadingLevel.H1) {
        node.children[0].text = node.children[0].text.toUpperCase();
        closestSubheadingText = null;
        closestHeadingText = toTitleCase(normalizeText(node.children[0].text), true);
      } else if (node.level === HeadingLevel.H2) {
        node.children[0].text = toTitleCase(node.children[0].text);
        closestSubheadingText = toTitleCase(normalizeText(node.children[0].text), true);
      }

      normalizedContent.push(node);
    } else if (node.type === PARAGRAPH_PLUGIN_ID) {
      if (isInlineParagraph(node)) {
        const shouldRemoveInlineProperty =
          normalizedContent.length === 0 ||
          normalizedContent[normalizedContent.length - 1].type !== HEADING_PLUGIN_ID;

        // If an inline paragraph is not preceded by a heading, remove the inline property
        if (shouldRemoveInlineProperty) {
          delete node.shouldForceInline;
        }
      } else if (Node.string(node.children[0]).trim() === ':') {
        i++;
        continue;
      } else if (
        normalizedContent.length !== 0 &&
        normalizedContent[normalizedContent.length - 1].type === HEADING_PLUGIN_ID &&
        !isInlineParagraph(node)
      ) {
        // If the last node was a heading, and this node is not an inline paragraph, add an
        // in line paragraph
        normalizedContent.push(createInlineParagraph());
      }

      // We have to loop through all of the children of the paragraph to see if any of them
      // is a field type. If it is, we check to see if it has a name. If it doesn't, we give it
      // a default name
      for (let childIndex = 0; childIndex < node.children.length; childIndex++) {
        const childNode = node.children[childIndex];
        if (isEligibleNamedField(childNode.type)) {
          const fieldName = childNode.name;
          if (
            fieldName == null ||
            String(fieldName).trim() === '' ||
            fieldNames.includes(fieldName)
          ) {
            // The default name of a field is, in order of priority, closest H2, H1, or 'Field'
            // followed by the number of default named fields of that type before it. If it
            // is named after the H1, then the numbering will start after the second H1 default
            // named field
            const defaultName: string = closestSubheadingText ?? closestHeadingText ?? 'Field';
            const counter = fieldNames.filter(
              (name) =>
                name === defaultName || (name.startsWith(defaultName) && endsWithNumber(name))
            ).length;
            if (defaultName === closestHeadingText && counter === 0) {
              childNode.name = defaultName;
            } else {
              childNode.name = defaultName + (counter + 1);
            }
          }

          fieldNames.push(childNode.name);
          node.children[childIndex] = childNode;
        }
      }
      normalizedContent.push(node);
    } else {
      normalizedContent.push(node);
    }
  }

  return normalizedContent;
};

export const worklistItemToSlateContent = (worklistItem: ReportWorklistItem): SlateContent => {
  if (worklistItem == null) {
    return [];
  }

  const { report } = worklistItem;
  let reportContent = null;
  // The backend generates a default report when it creates a case, so it's unlikely to be null.
  // The check here is a bit redundant, but it provides backwards-compatibility
  // for cases generated before the backend default report was updated to the Unified Editor standard.
  if (report == null || report.created === report.updated) {
    reportContent = generateEmptySlateContent();
  } else {
    reportContent = clone(report.content ?? []);
  }

  return normalizeSlateContent(reportContent);
};

export const applyPlaceholdersToSlateContentTemplate =
  ({ worklistItem }: { worklistItem: WorklistItem }): ((content: SlateContent) => SlateContent) =>
  (content) => {
    const newContent: Node[] = clone(content);
    const onElement = (elementNode: Node) => {
      if (elementNode.type === 'placeholder') {
        elementNode.children = [
          { text: resolvePlaceholderID(elementNode.placeholderID, worklistItem) },
        ];
        return elementNode;
      }

      return elementNode;
    };

    walkSlateContent(onElement)(newContent);

    return newContent;
  };

export const createAddendumFieldName = ({
  lastName,
  createdDate,
  addendumNumber,
  updatedDate,
  isAddendumLocked = false,
}: {
  lastName: Nullable<string>;
  createdDate: Nullable<Date>;
  addendumNumber: number;
  updatedDate?: Nullable<Date>;
  isAddendumLocked?: boolean;
}): string => {
  if (isAddendumLocked) {
    return `Addendum #${addendumNumber} is currently in progress by ${lastName ?? ''}`;
  } else {
    const date = updatedDate ?? createdDate;
    const formattedDate = date ? format(new Date(date), 'MM/dd/yyyy h:mm:ss a') : 'No date';
    return `Addendum #${addendumNumber} [${formattedDate} by ${lastName || ''}]`;
  }
};

export const formatAddendumContentByType = (content: AddendumContent): SlateContent | null => {
  // The backend supports several ways to store text, below we handle them all
  // so that they can be rendered by Slate.
  // If in the future we decide to support a different Rich Text Editor plugin
  // we'll be able to use this branching capabilities to adapt our code accordingly.
  // For example, we may decide to replace the Slate node with a different
  // Rich Text Editor component.
  switch (content.__typename) {
    // The text is already stored as Sirona Slate schema specification
    case 'AddendumSironaSlate':
      return content.sironaSlate ?? [];
    // The text is stored as plain text, we perform some very simple conversion
    // to make sure newlines are properly rendered
    case 'AddendumTextBlob':
      return content.textBlob.split('\n').map((text) => createParagraph(text));
    // The text type is not supported and we throw an error
    default:
      return null;
  }
};

export const generateAddendumSlateContentForAddendum = (addendum: Addendum): SlateContent => {
  const children = formatAddendumContentByType(addendum.content);

  if (children == null) {
    throw new Error(`Unreachable case: ${String(addendum.content.__typename)}`);
  }

  return children;
};

/**
 * Picklists can live on a report template or on a macro. Templates can contain an
 * infinite number of macros. We have decided to not support picklists as a first class
 * entity so the client has to do heavy lifting to get all of the possible picklists that
 * could be a part of a given report.
 *
 * This will break if a user copy/pastes a picklist into the report.
 *
 * Ticket: https://sironamedical.atlassian.net/browse/EN-3433
 */
export const getAllPicklistsForTemplate = (template: ReportTemplate): ReportPicklist[] => {
  if (template == null) {
    return [];
  }

  return [...template.picklists, ...template.macros.map((macro) => macro.picklists).flat()];
};

export const convertFieldsToUnified = (sections: SlateContent): SlateContent => {
  return sections.reduce((acc: SlateContent, curr: Node) => {
    if (curr.type !== 'field') {
      return acc;
    }

    const { label, children } = curr;
    const heading = {
      type: 'heading',
      level: HeadingLevel.H1,
      children: [{ text: label }],
    } as const;

    // wrap text nodes in paragraphs because they are being moved to the top level of the editor
    // which should only contain block nodes (slate will remove any inlines upon normalization).
    const normalizedChildren = children.map((child: Node) => {
      if (Text.isText(child)) {
        return createParagraphWithChildren([child]);
      }

      return child;
    });

    // the preamble is a special case because it is a field with no heading
    if (label === PREAMBLE_LABEL) {
      acc.push(...normalizedChildren);
      return acc;
    }

    const hasComparisonHeading = children.some(
      (child: Node) => normalizeText(Node.string(child)) === 'comparison'
    );
    if (hasComparisonHeading) {
      const comparisonIndex = normalizedChildren.findIndex(
        (node: Node) =>
          Text.isText(node.children?.[0]) &&
          normalizeText(node.children?.[0]?.text) === 'comparison'
      );
      const preComparisonSection = normalizedChildren.splice(0, comparisonIndex);
      const comparisonSection = normalizedChildren.splice(comparisonIndex + 1);

      if (!endsWithNewline(preComparisonSection)) {
        preComparisonSection.push(newline);
        acc.push(heading, ...preComparisonSection);
        return acc;
      }

      if (!endsWithNewline(comparisonSection)) {
        comparisonSection.push(newline);
        acc.push(
          { type: 'heading', level: 1, children: [{ text: 'comparison' }] },
          ...preComparisonSection
        );
        return acc;
      }
    }

    acc.push(heading, ...normalizedChildren);

    return acc;
  }, [] as SlateContent);
};

/**
 * Preamble is a special field that can be present at the top of the report before
 * any other headings. The preamble field does not have a heading in the report, but
 * we need to capture the content in a field so that it can be saved and submitted.
 */
export const PREAMBLE_LABEL = '';

// RP-6068 (M5a): a heading-like paragraph whose leading run is a RECOGNISED section
// label (the same matcher used everywhere in the heading-node deprecation). Only used
// when a `headingLabelSet` is supplied — without it, section splitting is node-only and
// byte-identical to the pre-deprecation behaviour.
const getHeadingLikeParagraphMatch = (
  node: Node,
  headingLabelSet?: Set<string>
): HeadingLikeMatch | null => {
  if (headingLabelSet == null) return null;
  if (node.type !== PARAGRAPH_PLUGIN_ID) return null;
  if ((node as { shouldForceInline?: boolean }).shouldForceInline === true) return null;
  const first = node.children?.[0];
  const text = Text.isText(first) ? first.text : '';
  return matchHeadingLikePrefix(text, headingLabelSet);
};

// The body carried inside a heading-like paragraph after its label run (e.g. the
// "clear" in "IMPRESSION: clear"), as a body paragraph. Returns null for a label-only
// line so section children come purely from the following nodes — matching how a
// heading NODE (which never holds body text) splits today.
const sliceParagraphBodyAfterLabel = (node: Node, labelEnd: number): Node | null => {
  const children = (node as { children?: Node[] }).children ?? [];
  const [first, ...rest] = children;
  const firstText = Text.isText(first) ? first.text : '';
  const remainder = firstText.slice(labelEnd).replace(/^\s+/, '');

  const bodyChildren: Node[] = [];
  if (Text.isText(first) && remainder.length > 0) {
    bodyChildren.push({ ...first, text: remainder });
  }
  bodyChildren.push(...rest.map((child) => clone(child)));

  const hasContent = bodyChildren.some((c) => !Text.isText(c) || c.text.trim().length > 0);
  if (!hasContent) return null;

  // Preserve any block-level props on the source paragraph (alignment, variant, etc.); only the
  // type and sliced children are overridden. `children` comes last so it always wins the spread.
  return {
    ...(node as object),
    type: PARAGRAPH_PLUGIN_ID,
    children: bodyChildren,
  } as unknown as Node;
};

/**
 * Splits report content into `field` sections. A section boundary is a top-level H1
 * heading NODE or — when `headingLabelSet` is supplied — a heading-like paragraph with a
 * recognised label (RP-6068 / M5a). Passing the label set keeps the emitted `label`
 * strings and section bodies stable as headings move from nodes to decorated paragraphs,
 * so the `generateImpression`/autosave payload survives the transition. With no label set
 * the result is byte-identical to the pre-deprecation, node-only behaviour.
 */
export const convertUnifiedToFields = (
  content: SlateContent,
  headingLabelSet?: Set<string>
): SlateContent => {
  const sections: SlateContent[] = [];
  const clonedContent: Node[] = clone(content);

  const boundaryAt = (node: Node): { label: string; body: Node | null } | null => {
    if (
      node.type === HEADING_PLUGIN_ID &&
      node.level === HeadingLevel.H1 &&
      node.children.length > 0
    ) {
      return { label: Node.string(node), body: null };
    }
    const match = getHeadingLikeParagraphMatch(node, headingLabelSet);
    if (match != null) {
      return { label: match.label, body: sliceParagraphBodyAfterLabel(node, match.end) };
    }
    return null;
  };

  const boundaries = clonedContent
    .map((node: Node, index: number) => {
      const boundary = boundaryAt(node);
      return boundary == null ? null : { index, label: boundary.label, body: boundary.body };
    })
    .filter((entry): entry is { index: number; label: string; body: Node | null } => entry != null);

  // Preamble: everything before the first boundary (no isCompleted, matching prior behaviour).
  const firstBoundaryIndex = boundaries.length > 0 ? boundaries[0].index : clonedContent.length;
  if (firstBoundaryIndex > 0) {
    sections.push({
      type: 'field',
      label: PREAMBLE_LABEL,
      children: clonedContent.slice(0, firstBoundaryIndex),
    });
  }

  for (let i = 0; i < boundaries.length; i++) {
    const { index, label, body } = boundaries[i];
    const endIndex = i + 1 < boundaries.length ? boundaries[i + 1].index : clonedContent.length;

    const bodyNodes: Node[] = [];
    if (body != null) bodyNodes.push(body);
    bodyNodes.push(...clonedContent.slice(index + 1, endIndex));

    const children = bodyNodes.map((contentNode) => {
      const cloned = clone(contentNode);
      return Text.isText(cloned) ? createParagraphWithChildren([cloned]) : cloned;
    });

    if (children.length === 0) {
      children.push(createParagraph());
    }

    const sectionIndex = sections.push({ type: 'field', label, children }) - 1;
    sections[sectionIndex].isCompleted = Node.string(sections[sectionIndex]) !== '';
  }

  return sections;
};

export const hasNestedSquareBrackets = (
  content: SlateContent,
  isInsideBracket: boolean
): boolean => {
  for (const node of content) {
    if (isInsideBracket && isSquareBracketType(node.type)) {
      return true;
    }

    if (isSquareBracketType(node.type)) {
      if (node.children && hasNestedSquareBrackets(node.children, true)) {
        return true;
      }
    } else if (node.children && hasNestedSquareBrackets(node.children, isInsideBracket)) {
      return true;
    }
  }

  return false;
};

export const insertClipboardDataAsFragment = (
  editor: Editor,
  event: ClipboardEvent
): SlateContent => {
  const clipboardData = event.clipboardData;
  if (clipboardData == null) {
    logger.info(
      `[Editable] Attempted to paste clipboard data into editor, but clipboard data was null`,
      {
        event,
      }
    );

    return;
  }

  const fragment = clipboardData.getData('application/x-slate-fragment');
  const plainText = clipboardData.getData('text/plain');

  // If we have a slate fragment that we are pasting, make sure it obeys the schema
  if (fragment != null && fragment !== '') {
    event.preventDefault();

    const decoded = decodeURIComponent(window.atob(fragment));
    const parsedFragment = JSON.parse(decoded);

    logger.info(
      `[Editable] Fragment pasted into editor with cursor at: "${getSurroundingTextString(
        editor
      )}"`,
      {
        fragment: parsedFragment,
        selection: editor?.selection ?? '',
      }
    );

    if (isNodeTypeIn(editor, PLACEHOLDER_PLUGIN_ID)) {
      // collect all text content from fragment
      let newText = '';
      for (const n of parsedFragment) {
        for (const [child] of Node.texts(n)) {
          newText += child.text;
        }
      }

      // insert text at current selection instead of inserting a nested node
      Transforms.insertText(editor, newText);

      return;
    }

    editor.insertFragment(parsedFragment);
    Editor.normalize(editor, { force: true });

    // If it's plain text, we split it into paragraphs and insert those into the editor
  } else if (plainText != null) {
    event.preventDefault();

    const paragraphs = plainText.split(/\r?\n/).map((text) => {
      return { type: PARAGRAPH_PLUGIN_ID, children: [{ text }] };
    });

    logger.info(
      `[Editable] Plain text pasted into editor with cursor at: "${getSurroundingTextString(
        editor
      )}`,
      {
        plainText,
        fragment: paragraphs,
        selection: editor?.selection ?? '',
      }
    );

    editor.insertFragment(paragraphs);
    Editor.normalize(editor, { force: true });
  }
};

export const getReportSMID = (worklistItem: LoadedReportWorklistItem) => {
  const { report } = worklistItem;
  if (report == null) {
    throw new Error('Cannot update a report without a smid.');
  }

  return report.smid;
};

export const enqueueAutoSaveErrorToast = ({
  enqueueToast,
  toastKey,
  reason = 'an unexpected error',
}: {
  enqueueToast: EnqueueToastFn;
  toastKey: string;
  reason?: string;
}) => {
  enqueueToast(
    createElement(
      'span',
      null,
      `Unable to autosave recent changes due to ${reason}. If the problem persists, contact `,
      createElement(SironaSupportLink),
      '.'
    ),
    {
      toastKey,
      autoHideDuration: null,
      yOffset: 45,
      icon: 'redWarning',
    }
  );
};

export const getCaseInfo = (worklistItem: LoadedReportWorklistItem) =>
  `${worklistItem?.patientName ?? 'Patient Name: N/A'}, ${worklistItem?.studyDescription ?? 'Study Description: N/A'} (${worklistItem?.accessionNumber ?? 'Accession Number: N/A'})`;

export const isAddendumEditorEditable = ({
  hasAddendum,
  isClaimedByMe,
  isDisabled,
  status,
}: {
  hasAddendum: boolean;
  isClaimedByMe: boolean;
  isDisabled: boolean;
  status: ReportStatuses;
}): boolean =>
  hasAddendum &&
  isClaimedByMe &&
  !isDisabled &&
  (ADDENDUM_EDITOR_EDITABLE_STATUSES as ReportStatuses[]).includes(status);
